import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { SortDirection, TextSortOrder } from '../../shared/enum/EnumRepository';
import { useTextListStore } from '../../shared/store/TextListStore';
import BlockWithSortVisible from './BlockWithSortVisible';
import { useTextIndexing } from '../../shared/hooks/useTextIndexing';
import { useUnsavedChanges } from '../../shared/hooks/useUnsavedChanges';
import TextFontSizeToggle from './TextFontSizeToggle';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import TextOverviewEditActions from './TextOverviewEditActions';

const TextColumnInfo: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { requestExitEditMode } = useUnsavedChanges();
  const sortData = useTextListStore((s) => s.sortData);
  const setSortData = useTextListStore((s) => s.setSortData);
  const { isIndexingMode } = useTextIndexing();
  const isLargeBody = useTextViewStore((s) => s.isLargeBody);

  const handleDirectionChange = (field: TextSortOrder) => {
    if (isEditMode) {
      requestExitEditMode({ confirmWhenClean: true });
      return;
    }

    // 현재 정렬 필드와 같으면 방향만 변경, 다르면 해당 필드로 정렬하고 ASC로 설정
    if (sortData.sort === field) {
      setSortData({
        sort: field,
        direction: sortData.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      });
    } else {
      setSortData({ sort: field, direction: SortDirection.ASC });
    }
  };

  return (
    <ColumnInfoComp>
      <InfoContainer>
        <TitleBlock onClick={() => handleDirectionChange(TextSortOrder.TITLE)}>
          Title
          <SelectDirection
            $isASC={sortData.sort === TextSortOrder.TITLE && sortData.direction === SortDirection.ASC}
            $isSortField={sortData.sort === TextSortOrder.TITLE}
          >
            ▼
          </SelectDirection>
        </TitleBlock>
        <BlockWithSortVisible
          width={"2"}
          isColumn={true}
          field={TextSortOrder.AUTHOR}
          value={"Author"}
        />
        <BlockWithSortVisible
          width={"1"}
          isColumn={true}
          field={TextSortOrder.YEAR}
          value={"Year"}
        />
        <SpaceBlock />
      </InfoContainer>
      <OverviewBlock $isExtended={isLargeBody}>
        {isIndexingMode ? 'Indexing' : 'Overview'}
        {!isIndexingMode && !isEditMode && <TextFontSizeToggle />}
        <TextOverviewEditActions />
      </OverviewBlock>
    </ColumnInfoComp>
  )
}

const ColumnInfoComp = styled.article`
  width: 100%;
  height: 38px;
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_02};

  ${({ theme }) => theme.typography.Body_03_2};
`;

const InfoContainer = styled.article`
  flex: 1;
  width: auto;
  height: 34px;
  display: flex;
`;

const TitleBlock = styled.div`
  flex: 3;

  display: flex;
  align-items: center;
  padding-left: 4px;
  cursor: pointer;
  user-select: none;
`

const SelectDirection = styled.span<{ $isSortField: boolean, $isASC: boolean }>`
  margin-left: 4px;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme, $isSortField }) => $isSortField ? theme.colors.color_Gray_03 : theme.colors.color_Gray_05};
  transform: ${({ $isASC, $isSortField }) => {
    if (!$isSortField) return "scale(0.7)"; // 기본: dim 처리된 ▼
    return $isASC ? "scale(0.7)" : "scaleY(-1) scale(0.7)"; // ASC면 그대로, DESC면 뒤집기
  }};
  transition: transform 0.6s ease-in-out;
`;

const SpaceBlock = styled.div`
  flex: 0.15;
  
  display: flex;
  align-items: center;
  justify-content: center;
`

const OverviewBlock = styled.div<{ $isExtended: boolean }>`
  flex: 0 0 ${({ $isExtended }) => ($isExtended ? 'calc(38vw * 1.2)' : '38vw')};
  max-width: ${({ $isExtended }) => ($isExtended ? '720px' : '600px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 2px;
  user-select: none;
  box-sizing: border-box;
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
`

export default TextColumnInfo;
